import {
	CheckCircleFilled,
	CheckCircleOutlined,
	CloseCircleOutlined,
	EditOutlined,
	InboxOutlined,
	SearchOutlined,
	SyncOutlined,
	UploadOutlined,
	UsergroupAddOutlined,
	WarningOutlined,
} from '@ant-design/icons';
import {
	Button,
	Col,
	Divider,
	Dropdown,
	Menu,
	Modal,
	Result,
	Row,
	Select,
	Space,
	Steps,
	Tooltip,
} from 'antd';
import Upload, { UploadFile } from 'antd/es/upload';
import Dragger from 'antd/es/upload/Dragger';
import { employeeApi } from 'Api/employee';
import { Loader } from 'components/Global';
import { PermissionContext } from 'components/Global/AuthLayout';
import Ellipse from 'components/Global/Ellipse';
import SearchComponent from 'components/Global/SearchComponent';
import SimpleVirtualTable from 'components/Global/VirtualTable';
import { useCallback, useContext, useMemo, useState } from 'react';
import { DeleteActionSvg, DownloadSvg } from 'utils/svgs';
import { convertArrayToExcel, toastText } from 'utils/utils';
import BulkSalaryImport from './BulkSalaryAddModal';
import styles from './index.module.scss';
import './index.scss';

const { Step } = Steps;

const statusOptions = [
	{
		label: 'All Status',
		value: '',
	},
	{
		label: 'Active',
		value: 'ACTIVE',
	},
	{
		label: 'Suspended',
		value: 'SUSPENDED',
	},
	{
		label: 'Redundant',
		value: 'REDUNDANT',
	},
	{
		label: 'Retired',
		value: 'RETIRED',
	},
	{
		label: 'Resigned',
		value: 'RESIGNED',
	},
	{
		label: 'Dismissed',
		value: 'DISMISSED',
	},
	{
		label: 'Deceased',
		value: 'DECEASED',
	},
];

const steps = [
	{
		title: 'Upload File',
	},
	{
		title: 'Validation Results',
	},
	{
		title: 'Correct Data',
	},
	{
		title: 'Confirmation',
	},
];

const staffCodeOptions = [
	{
		label: 'All Staff Code',
		value: '',
	},
	{
		label: 'Hourly',
		value: 'HOURLY',
	},
	{
		label: 'Monthly',
		value: 'MONTHLY',
	},
];

type Props = {
	searchValue: string;
	handleSearch: (value: string) => void;
	statusFilterValue: string;
	handleStatusFilter: (value: string) => void;
	handleSearchApi: (value: string) => void;
	staffCodeFilterValue: string;
	handleStaffCodeFilter: (value: string) => void;
	fetchRecords: any;
	fetchEmployees: any;
	isLoading: any;
	totalRecords: number;
	branchIds: string[];
};

const EmployeeHeader = (props: Props) => {
	const {
		searchValue,
		handleSearch,
		statusFilterValue,
		handleStatusFilter,
		handleSearchApi,
		handleStaffCodeFilter,
		staffCodeFilterValue,
		fetchEmployees,
		isLoading,
		totalRecords,
		branchIds,
	} = props;

	const permissionContext = useContext(PermissionContext);

	const isAdd =
		permissionContext.allowedPermissions.includes('Add_Employment');

	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [isUploadLoading, setIsUploadLoading] = useState(false);

	const [isModalVisible, setIsModalVisible] = useState(false);
	const [validationResults, setValidationResults] = useState([]);

	const [finalData, setFinalData] = useState([]);

	const [isErrorModal, setIsErrorModal] = useState(false);
	const [errorData, setErrorData] = useState<any>(null);

	const [currentStep, setCurrentStep] = useState(0);
	const [isImportLoading, setIsImportLoading] = useState(false);

	const [downloadLoading, setDownloadLoading] = useState(false);
	const [NrcUpdateObject, setNrcUpdateObject] = useState<any>(null);

	const next = async () => {
		if (currentStep == 1) {
			const data = validationResults.filter((item: any) => item._isValid);
			setFinalData(data);
		}
		if (currentStep == 2) {
			await importEmployees(finalData);
		} else {
			setCurrentStep((prev: any) => {
				return Math.min(prev + 1, steps.length - 1);
			});
		}
	};

	const prev = () => {
		setCurrentStep((prev) => Math.max(prev - 1, 0));
		if (currentStep === 1) {
			setFileList([]);
		}
	};

	const getClassName = (isValid: boolean) => {
		if (!isValid) {
			return 'required-color';
		} else {
			return '';
		}
	};

	const handleDelete = useCallback(
		(index: number) => {
			// Use slicing for immutable updates (more efficient than filter for large arrays)
			const updatedResults = [
				...validationResults.slice(0, index),
				...validationResults.slice(index + 1),
			];
			setValidationResults(updatedResults);

			if (currentStep === 2) {
				const updatedFinalData = [
					...finalData.slice(0, index),
					...finalData.slice(index + 1),
				];
				setFinalData(updatedFinalData);
			}

			toastText('Record deleted Successfully', 'success');
		},
		[validationResults, finalData, currentStep]
	);

	const isRecordUpdatable = (record: any) => {
		if (!NrcUpdateObject || !record['National Registration Number'])
			return false;

		const matchingEntry = NrcUpdateObject.find(
			(item: any) =>
				Object.keys(item)[0] === record['National Registration Number']
		);

		return (
			matchingEntry &&
			matchingEntry[record['National Registration Number']]?.isUpdated ===
				true
		);
	};

	const isStaffCodeChange = (record: any) => {
		if (!NrcUpdateObject || !record['National Registration Number'])
			return false;

		const matchingEntry = NrcUpdateObject.find(
			(item: any) =>
				Object.keys(item)[0] === record['National Registration Number']
		);

		return (
			matchingEntry &&
			matchingEntry[record['National Registration Number']]
				?.isStaffCodeChange === true
		);
	};

	const columns = useMemo(() => {
		if (!validationResults.length) return [];
		return [
			{
				title: 'Status',
				key: '_validationStatus',
				fixed: 'left',
				width: 80,
				className: 'bg-white',
				render: (_: any, record: any) =>
					record._isValid ? (
						<div
							className="text-green-500 bg-table"
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								color: 'green',
							}}
						>
							<CheckCircleOutlined />
						</div>
					) : (
						<div
							className="text-red-500 bg-table"
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								color: 'red',
							}}
						>
							<CloseCircleOutlined />
						</div>
					),
			},
			{
				title: 'Validation',
				key: '_errors',
				width: 105,
				className: 'bg-white',
				fixed: 'left',
				render: (_: any, record: any) => {
					return !record._isValid ? (
						<p
							onClick={() => {
								setIsErrorModal(true);
								setErrorData(record);
							}}
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								color: 'orange',
							}}
							className={`pointer ${getClassName(
								record._isValid
							)}`}
						>
							<WarningOutlined />
						</p>
					) : null;
				},
			},
			{
				title: 'Action',
				key: 'action',
				width: 80,
				fixed: 'left',
				render: (_: any, record: any, index: number) => {
					return (
						<div
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
							}}
						>
							<Tooltip title="Delete">
								<p
									className="cursor-pointer"
									onClick={() => {
										handleDelete(index);
									}}
								>
									<DeleteActionSvg />
								</p>
							</Tooltip>
						</div>
					);
				},
			},
			...Object.keys(validationResults[0] || {})
				.filter((key) => !key.startsWith('_'))
				.map((key) => ({
					title: key,
					dataIndex: key,
					key: key,
					width: 150,
					ellipsis: true,
					className: 'bg-table bg-white',
					...((key === 'First Name' || key === 'Staff Code') && {
						fixed: 'left',
						width: 180,
					}),
					render: (text: string, record: any) => {
						const isFirstNameWithEdit =
							key === 'First Name' && isRecordUpdatable(record);

						const isStaffCodeWithWarning =
							key === 'Staff Code' && isStaffCodeChange(record);

						return (
							<div
								style={{
									minWidth: 150,
									display: 'flex',
									alignItems: 'center',
								}}
								className={`${getClassName(record._isValid)}`}
							>
								<Ellipse
									maxLength={20}
									message={text}
									isTooltip={true}
									tooltipMessage={text}
									key={text}
								/>

								{isFirstNameWithEdit && (
									<Tooltip title="This record will be updated due to matching NRC">
										<EditOutlined
											style={{
												marginLeft: 10,
												color: '#584495',
											}}
										/>
									</Tooltip>
								)}

								{isStaffCodeWithWarning && (
									<Tooltip
										title={`This employee is currently ${
											text?.toLowerCase() === 'hourly'
												? 'Monthly'
												: 'Hourly'
										}. Changing the Staff Code will switch them to ${
											text?.toLowerCase() === 'hourly'
												? 'Hourly'
												: 'Monthly'
										}, and all calculations will update accordingly.`}
									>
										<WarningOutlined
											style={{
												marginLeft: 10,
												color: 'orange',
											}}
										/>
									</Tooltip>
								)}
							</div>
						);
					},
				})),
		];
	}, [handleDelete, validationResults]);

	// Helper function to check if a record is marked for update

	const downloadEmployeeCsv = async () => {
		try {
			const response = await employeeApi.downloadEmployeeCsvTemplate();
			const url = window.URL.createObjectURL(
				new Blob([response.data], {
					type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				})
			);

			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `employees.xlsx`);
			document.body.appendChild(link);
			link.click();
			link.remove();
		} catch (err) {
			toastText(
				'Something went wrong in downloading excel template',
				'error'
			);
		}
	};

	const downloadEmployeeCsvWithData = async () => {
		try {
			setDownloadLoading(true);
			const query = {
				status: statusFilterValue,
				staffCode: staffCodeFilterValue,
				branchIds: branchIds,
			};
			const response =
				await employeeApi.downloadEmployeeCsvWithDataTemplate(query);
			const url = window.URL.createObjectURL(
				new Blob([response.data], {
					type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				})
			);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `employees_data.xlsx`);
			document.body.appendChild(link);
			link.click();
			link.remove();
			// Clean up the URL object
			window.URL.revokeObjectURL(url);
		} catch (err) {
			toastText(
				'Something went wrong in downloading excel template',
				'error'
			);
		} finally {
			setDownloadLoading(false);
		}
	};

	const handleUpload = async (file: any) => {
		// Detect file type
		const fileExtension = file.name
			.substring(file.name.lastIndexOf('.'))
			.toLowerCase();
		const isExcel =
			fileExtension === '.xlsx' ||
			fileExtension === '.xls' ||
			file.type ===
				'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
			file.type === 'application/vnd.ms-excel';

		// Validate file type - only Excel files are accepted
		const allowedExtensions = ['.xlsx', '.xls'];
		const allowedMimes = [
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			'application/vnd.ms-excel',
			'application/octet-stream',
			'application/xls',
			'application/xlsx',
		];

		const hasValidExtension = allowedExtensions.includes(fileExtension);
		const hasValidMimeType = file.type ? allowedMimes.includes(file.type) : false;
		const isOctetStreamWithExcelExt = 
			file.type === 'application/octet-stream' && hasValidExtension;

		if (!hasValidExtension && !hasValidMimeType && !isOctetStreamWithExcelExt) {
			toastText(
				'Invalid file format. Only Excel files (.xls, .xlsx) are accepted',
				'error'
			);
			setFileList([]);
			return false;
		}

		// Only Excel files are accepted now
		try {
			setIsUploadLoading(true);

			let formData = new FormData();
			formData.append('documentName', 'employee_file');
			formData.append('file', file);

			const validatedData: any =
				await employeeApi.validateDataFromAPI(formData);
			const apiResponseData = validatedData.data.data;

			const mappedNrcData = apiResponseData.map(
				(item: Record<string, any>) => {
					return {
						[item['National Registration Number']]: {
							isUpdated: item.isUpdated,
							isStaffCodeChange: item.isStaffCodeChange,
						},
					};
				}
			);
			setNrcUpdateObject(mappedNrcData);

			const cleanedData = apiResponseData.map(
				(item: Record<string, any>) => {
					return Object.fromEntries(
						Object.entries(item).filter(([key]) => {
							return (
								key !== 'isUpdated' &&
								key !== '!isStaffCodeChange'
							);
						})
					);
				}
			);

			setValidationResults(cleanedData);
			setIsModalVisible(true);
			setCurrentStep(1);
		} catch (error: any) {
			// Show backend error message if available
			const errorMessage =
				error?.response?.data?.message ||
				error?.response?.data?.error?.message ||
				error?.message ||
				'Failed to process file. Please try again';
			toastText(errorMessage, 'error');
			setFileList([]);
			setCurrentStep(0);
		} finally {
			setIsUploadLoading(false);
		}
		return false;
	};

	const importEmployees = async (data: any) => {
		try {
			setIsImportLoading(true);

			const blob = convertArrayToExcel(data);

			const formData = new FormData();
			formData.append('file', blob, 'data.xlsx');

			const response = await employeeApi.importEmployeesWithFormData(
				formData
			);
			// const response = await employeeApi.importEmployees({
			// 	employees: data,
			// });
			toastText(response.data.message, 'success');
			setCurrentStep(3);
		} catch (error: any) {
			const errorMessage =
				error?.response?.data?.message ||
				'Something went wrong please while importing employees';

			toastText(errorMessage, 'error');
		} finally {
			setIsImportLoading(false);
		}
	};

	const handleModalClose = () => {
		setIsModalVisible(false);
		setErrorData(false);
		setFileList([]);
		setCurrentStep(0);
		setFinalData([]);
		setErrorData([]);
		setIsErrorModal(false);
		setIsImportLoading(false);
		setIsUploadLoading(false);
	};

	const handleImportMore = () => {
		setErrorData(false);
		setFileList([]);
		setCurrentStep(0);
		setFinalData([]);
		setErrorData([]);
		setIsErrorModal(false);
		setIsImportLoading(false);
		setIsUploadLoading(false);
	};
	const menu = (
		<>
			<Menu>
				<p style={{ padding: '5px', color: '#584495' }}>
					<span style={{ fontSize: '18px' }}>
						<UsergroupAddOutlined />
					</span>{' '}
					Employee Action
				</p>
				<Tooltip title="Employee Action">
					<Menu.Item
						style={{ backgroundColor: 'white' }}
						key="2"
						onClick={() => setIsModalVisible(true)}
						icon={<UploadOutlined />}
					>
													Upload Employee File (Excel)
					</Menu.Item>
					<Divider style={{ marginBottom: '3px' }} />
				</Tooltip>
				<BulkSalaryImport />
			</Menu>
		</>
	);

	return (
		<>
			<div className={styles['employee-header']}>
				<Space>
					<SearchComponent
						className={styles['employee-header-item']}
						placeHolder="Search by code or name..."
						prefixIcon={<SearchOutlined />}
						handleChange={handleSearch}
						handleChangeApi={handleSearchApi}
						value={searchValue}
						size="large"
					/>
				</Space>
				<Space>
					<Select
						className={styles['employee-header-item']}
						value={statusFilterValue}
						options={statusOptions}
						onChange={(value) => {
							handleStatusFilter(value);
						}}
						size="large"
						placeholder="Select Status"
						style={{
							maxWidth: '200px',
						}}
					/>
				</Space>
				<Space>
					<Select
						className={styles['employee-header-item']}
						value={staffCodeFilterValue}
						options={staffCodeOptions}
						onChange={(value) => {
							handleStaffCodeFilter(value);
						}}
						size="large"
						placeholder="Select Staff Code"
						style={{
							maxWidth: '200px',
						}}
					/>
				</Space>

				<div className={styles['refresh-buttons']}>
					<Tooltip title="Refresh all the Employee Details">
						<SyncOutlined
							style={{
								fontSize: '20px',
								cursor: isLoading ? 'not-allowed' : 'pointer',
								color: '#584495',
								transition: 'transform 0.3s',
								marginRight: '10px',
							}}
							onClick={!isLoading ? fetchEmployees : undefined}
						/>
					</Tooltip>
				</div>
				{totalRecords > 0 ? (
					<div
						style={{
							marginRight: '10px',
						}}
					>
						<Tooltip title="Download excel with employee Data">
							<p
								className={styles['csv-button']}
								onClick={
									!downloadLoading
										? downloadEmployeeCsvWithData
										: undefined
								}
							>
								{downloadLoading ? (
									<>
										<Loader />
									</>
								) : (
									<DownloadSvg />
								)}
							</p>
						</Tooltip>
					</div>
				) : null}
				{isAdd && (
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							border: '1px solid grey',
							borderRadius: '5px',
						}}
					>
						<Dropdown overlay={menu} trigger={['click']}>
							<p className={styles['menu-icon']}>
								<span
									style={{
										fontSize: '3rem',
										color: 'grey',
										marginBottom: '15px',
										padding: '5px 10px',
									}}
								>
									...
								</span>
							</p>
						</Dropdown>
					</div>
				)}
			</div>

			<Modal
				open={isModalVisible}
				onCancel={handleModalClose}
				width="1400px"
				centered
				footer={null}
				destroyOnClose
				rootClassName="uploadCsvModal"
				closable={false}
				maskClosable={false}
				// style={{
				// 	maxHeight: 'calc(100vh - 200px)',
				// 	display: 'flex',
				// 	flexDirection: 'column',
				// }}
			>
				<div>
					<Steps current={currentStep}>
						{steps.map((step, index) => (
							<Step key={index} title={step.title} />
						))}
					</Steps>

					<div
						style={{
							marginTop: '20px',
							marginBottom: '20px',
							padding: '20px',
							border: '1px solid #d9d9d9',
							borderRadius: '5px',
							flex: 1,
							overflow: 'hidden',
							display: 'flex',
							flexDirection: 'column',
							minHeight: '200px',
							justifyContent: 'center',
						}}
					>
						{currentStep === 0 && (
							<div className={styles['document-upload']}>
								{isUploadLoading ? (
									<Loader />
								) : (
									<>
										<Row>
											<Col
												span={24}
												style={{
													paddingBottom: '10px',
													paddingTop: '0px',
													display: 'flex',
													justifyContent: 'end',
												}}
											>
												<Tooltip title="Download Employee Excel Template">
													<p
														className={
															styles[
																'template-link'
															]
														}
														onClick={
															downloadEmployeeCsv
														}
													>
														<div
															style={{
																display: 'flex',
																justifyContent:
																	'center',
															}}
														>
															<DownloadSvg />
														</div>
														Download Template
													</p>
												</Tooltip>
											</Col>
										</Row>
										<Dragger
											name="file"
											multiple={false}
											accept=".xlsx,.xls"
											fileList={fileList}
											beforeUpload={(file) => {
												const fileExtension = file.name
													.substring(
														file.name.lastIndexOf(
															'.'
														)
													)
													.toLowerCase();
												// Only Excel files are accepted
												const allowedExtensions = ['.xlsx', '.xls'];
												const allowedMimes = [
													'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
													'application/vnd.ms-excel',
													'application/octet-stream',
													'application/xls',
													'application/xlsx',
												];
												const hasValidExtension = allowedExtensions.includes(fileExtension);
												const hasValidMimeType = file.type ? allowedMimes.includes(file.type) : false;
												const isOctetStreamWithExcelExt = 
													file.type === 'application/octet-stream' && hasValidExtension;
												const isExcel = hasValidExtension || hasValidMimeType || isOctetStreamWithExcelExt;
												if (!isExcel) {
													toastText(
														'Invalid file format. Only Excel files (.xls, .xlsx) are accepted',
														'error'
													);
													return Upload.LIST_IGNORE;
												}
												const isExcelOld =
													fileExtension === '.xlsx' ||
													fileExtension === '.xls' ||
													file.type ===
														'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
													file.type ===
														'application/vnd.ms-excel';

												if (!isExcel) {
													toastText(
														'Invalid file format. Only Excel files (.xls, .xlsx) are accepted',
														'error'
													);
													return Upload.LIST_IGNORE;
												}

												handleUpload(file);
												return false;
											}}
											onChange={(info) => {
												let newFileList = [
													...info.fileList,
												];

												// Limit to one file
												newFileList =
													newFileList.slice(-1);

												setFileList(newFileList);
											}}
										>
											<p className="ant-upload-drag-icon">
												<InboxOutlined />
											</p>
											<p className="ant-upload-text">
												Click or drag Excel file to
												this area to upload
											</p>
											<p className="ant-upload-hint">
												Support for a single Excel file upload (up to{' '}
												<span
													style={{ color: '#584495' }}
												>
													200 records
												</span>
												)
											</p>
										</Dragger>
									</>
								)}
							</div>
						)}

						{currentStep === 1 && (
							<div className="validation-results">
								<SimpleVirtualTable
									validationResults={validationResults}
									columns={columns}
									onDelete={handleDelete}
									onErrorClick={(record) => {
										setIsErrorModal(true);
										setErrorData(record);
									}}
									NrcUpdateObject={NrcUpdateObject}
									getClassName={getClassName}
								/>
							</div>
						)}

						{currentStep === 2 && (
							<div className="validation-results">
								<SimpleVirtualTable
									validationResults={finalData}
									columns={columns.filter((_, index:number) => index !== 1 && index !== 2)}
									onDelete={handleDelete}
									onErrorClick={(record) => {
										setIsErrorModal(true);
										setErrorData(record);
									}}
									NrcUpdateObject={NrcUpdateObject}
									getClassName={getClassName}
								/>
							</div>
						)}
						{currentStep === 3 && (
							<Result
								status="success"
								title={`The process of importing ${
									finalData.length > 1
										? 'employees'
										: 'an employee'
								} has started.`}
								subTitle={`Processing ${
									finalData.length > 1
										? 'employees'
										: 'employee'
								} record. Only ${
									finalData.length
								} validated and imported. This may take a moment, Please wait....`}
								icon={
									<CheckCircleFilled
										style={{ color: '#584495' }}
									/>
								}
								extra={[
									<Button
										type="primary"
										key="console"
										onClick={handleImportMore}
									>
										Import More
									</Button>,
									<Button
										key="buy"
										onClick={handleModalClose}
									>
										Close
									</Button>,
								]}
							/>
						)}
					</div>

					{currentStep !== 3 && (
						<div
							style={{
								marginTop: '20px',
								display: 'flex',
								justifyContent: 'end',
								gap: '15px',
							}}
						>
							<Button onClick={handleModalClose}>Cancel</Button>
							{currentStep !== 0 && (
								<Button
									disabled={
										currentStep === 0 ||
										isImportLoading ||
										isUploadLoading
									}
									onClick={prev}
									type="primary"
								>
									Previous
								</Button>
							)}
							{fileList.length > 0 && (
								<Button
									disabled={
										currentStep === steps.length - 1 ||
										isImportLoading ||
										isUploadLoading ||
										(currentStep === 1 &&
											validationResults.length === 0) ||
										(currentStep === 2 &&
											finalData.length === 0)
									}
									type="primary"
									onClick={next}
								>
									{currentStep === 2
										? 'Import Employees'
										: 'Next'}
								</Button>
							)}
						</div>
					)}
				</div>
			</Modal>

			{errorData && isErrorModal && (
				<Modal
					open={isErrorModal}
					onCancel={() => {
						setErrorData(null);
						setIsErrorModal(false);
					}}
					destroyOnClose
					rootClassName="uploadCsvModal"
					width="90%"
					footer={null}
				>
					<ul
						className="error-list"
						style={{
							listStyleType: 'none',
							padding: '10px',
							borderRadius: '5px',
							border: '1px solid #d4d4d4',
							color: '#721c24',
							fontFamily: 'Arial, sans-serif',
							maxHeight: '450px',
							overflowY: 'auto',
						}}
					>
						{Object.entries(errorData._validationErrors).map(
							([field, error]: any) => (
								<li
									key={field}
									style={{
										padding: '8px 12px',
										borderBottom: '1px solid #d4d4d4',
									}}
								>
									<span
										style={{
											fontWeight: 'bold',
											color: '#b02a2a',
										}}
									>
										{field}
									</span>{' '}
									:{' '}
									<span
										style={{
											fontStyle: 'italic',
											color: 'black',
										}}
									>
										{error}
									</span>
								</li>
							)
						)}
					</ul>
				</Modal>
			)}
		</>
	);
};

export default EmployeeHeader;
